import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (v, digits = 2, prefix = "") => {
    if (v === null || v === undefined || Number.isNaN(v)) return "—";
    return prefix + Number(v).toFixed(digits);
};

const ConfidenceBadge = ({ conf }) => {
    const map = {
        high: { bg: "bg-emerald-500/15", txt: "text-emerald-300", ring: "ring-emerald-400/40", label: "HIGH CONFIDENCE" },
        medium: { bg: "bg-amber-500/15", txt: "text-amber-300", ring: "ring-amber-400/40", label: "MEDIUM CONFIDENCE" },
    };
    const s = map[conf] || map.medium;
    return (
        <span data-testid="apollo-confidence-badge" className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold tracking-widest uppercase rounded ${s.bg} ${s.txt} ring-1 ${s.ring}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
            {s.label}
        </span>
    );
};

const DirectionBadge = ({ dir }) => {
    const isLong = dir === "long";
    return (
        <span data-testid="apollo-direction-badge" className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold tracking-wider uppercase ${isLong ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`}>
            {isLong ? "▲ LONG" : "▼ SHORT"}
        </span>
    );
};

const StatusBanner = ({ status, reasoning }) => {
    if (status === "signal") return null;
    const label = status === "no_setup" ? "STANDING DOWN" : status === "no_data" ? "DATA WAITING" : status === "engine_unavailable" ? "ENGINE BOOTING" : "MONITORING";
    return (
        <div data-testid="apollo-standby-banner" className="border border-amber-500/25 bg-amber-500/5 rounded-lg px-4 py-3 mb-4">
            <div className="flex items-center gap-2 text-amber-300 text-xs font-semibold tracking-widest uppercase mb-1">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                {label}
            </div>
            <p className="text-neutral-400 text-xs">{reasoning || "Apollo is scanning the 4H gold chart for setup confluence. Signals only fire when 2+ gates align."}</p>
        </div>
    );
};

const HeroSignal = ({ signal, lastCheck }) => {
    if (!signal || signal.status !== "signal") {
        return (
            <div data-testid="apollo-hero-idle" className="p-6 md:p-8 rounded-2xl bg-gradient-to-br from-neutral-950 to-neutral-900 border border-neutral-800">
                <div className="text-neutral-500 text-xs uppercase tracking-widest mb-2">Latest Setup</div>
                <div className="text-3xl md:text-4xl font-serif text-neutral-300 mb-1">Waiting for confluence</div>
                <p className="text-neutral-500 text-sm mt-2">Apollo runs a fresh scan every 4 hours. Only high-conviction Wyckoff + RSI + EMA + macro confluences trigger a signal.</p>
                <div className="mt-4 text-[11px] uppercase tracking-widest text-neutral-600">Last scan: {lastCheck?.timestamp ? new Date(lastCheck.timestamp).toLocaleString() : "—"}</div>
            </div>
        );
    }
    const s = signal;
    return (
        <motion.div
            data-testid="apollo-hero-signal"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 md:p-8 rounded-2xl bg-gradient-to-br from-amber-900/20 via-neutral-950 to-neutral-900 border border-amber-500/20 shadow-[0_0_60px_-30px_rgba(251,191,36,0.4)]"
        >
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <ConfidenceBadge conf={s.confidence} />
                <DirectionBadge dir={s.direction} />
                <span className="text-[10px] text-neutral-500 uppercase tracking-widest ml-auto">
                    {new Date(s.sent_at).toLocaleString()}
                </span>
            </div>

            <div className="text-neutral-500 text-xs uppercase tracking-widest mb-1">{s.asset} · Setup Score {s.score}/4</div>
            <div className="text-4xl md:text-5xl font-serif text-amber-200 mb-1">${fmt(s.entry)}</div>
            <div className="text-sm text-neutral-400 mt-1">{s.reasoning}</div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
                <div data-testid="apollo-hero-entry" className="rounded-lg bg-neutral-900/60 border border-neutral-800 p-3">
                    <div className="text-[10px] text-neutral-500 uppercase tracking-widest">Entry</div>
                    <div className="text-lg text-amber-200 font-mono">${fmt(s.entry)}</div>
                </div>
                <div data-testid="apollo-hero-sl" className="rounded-lg bg-red-950/30 border border-red-500/20 p-3">
                    <div className="text-[10px] text-red-400 uppercase tracking-widest">Stop Loss</div>
                    <div className="text-lg text-red-200 font-mono">${fmt(s.stop_loss)}</div>
                </div>
                {(s.take_profit || []).map((tp, i) => (
                    <div key={i} data-testid={`apollo-hero-tp${i + 1}`} className="rounded-lg bg-emerald-950/30 border border-emerald-500/20 p-3">
                        <div className="text-[10px] text-emerald-400 uppercase tracking-widest">TP{i + 1} · RR 1:{i === 0 ? s.risk_reward_tp1 : i === 1 ? s.risk_reward_tp2 : s.risk_reward_tp3}</div>
                        <div className="text-lg text-emerald-200 font-mono">${fmt(tp)}</div>
                    </div>
                ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
                {(s.gates_fired || []).map(g => (
                    <span key={g} className="px-2 py-1 rounded bg-neutral-800/70 border border-neutral-700 text-[10px] uppercase tracking-widest text-neutral-400">
                        {g.replace(/_/g, " ")}
                    </span>
                ))}
            </div>

            <div className="mt-4 text-[11px] text-neutral-500">
                Suggested risk per trade: <span className="text-neutral-300">{s.risk_pct_suggestion}%</span> · ATR: <span className="text-neutral-300">${fmt(s.atr_value)}</span>
            </div>
        </motion.div>
    );
};

const MacroStrip = ({ signal }) => {
    if (!signal) return null;
    const items = [
        { label: "Price", val: signal.price_now ? `$${fmt(signal.price_now)}` : "—" },
        { label: "Wyckoff", val: signal.wyckoff_phase || "—" },
        { label: "RSI Div", val: signal.rsi_divergence || "—" },
        { label: "EMA", val: signal.ma_alignment || "—" },
        { label: "DXY", val: signal.dxy_value ? `${fmt(signal.dxy_value)} ${signal.macro_bias === 'bullish' ? '↓' : signal.macro_bias === 'bearish' ? '↑' : ''}` : "—" },
        { label: "US10Y", val: signal.us10y_value ? `${fmt(signal.us10y_value, 3)}` : "—" },
    ];
    return (
        <div data-testid="apollo-macro-strip" className="mt-6 grid grid-cols-3 md:grid-cols-6 gap-2">
            {items.map(i => (
                <div key={i.label} className="rounded-lg bg-neutral-900/40 border border-neutral-800 px-3 py-2">
                    <div className="text-[10px] text-neutral-500 uppercase tracking-widest">{i.label}</div>
                    <div className="text-sm text-neutral-300 capitalize">{i.val}</div>
                </div>
            ))}
        </div>
    );
};

const PerformanceCard = ({ perf }) => {
    if (!perf) return null;
    return (
        <div data-testid="apollo-performance-card" className="p-5 md:p-6 rounded-2xl bg-neutral-950 border border-neutral-800">
            <div className="text-neutral-500 text-xs uppercase tracking-widest mb-3">Track Record</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                    <div className="text-[10px] text-neutral-500 uppercase">Total Signals</div>
                    <div className="text-2xl text-neutral-100 font-mono">{perf.total_signals || 0}</div>
                </div>
                <div>
                    <div className="text-[10px] text-neutral-500 uppercase">Win Rate</div>
                    <div className="text-2xl text-emerald-300 font-mono">{perf.win_rate_pct !== null && perf.win_rate_pct !== undefined ? `${perf.win_rate_pct}%` : "—"}</div>
                </div>
                <div>
                    <div className="text-[10px] text-neutral-500 uppercase">Wins / Losses</div>
                    <div className="text-2xl text-neutral-100 font-mono">{perf.wins || 0} / {perf.losses || 0}</div>
                </div>
                <div>
                    <div className="text-[10px] text-neutral-500 uppercase">Pending</div>
                    <div className="text-2xl text-amber-300 font-mono">{perf.pending || 0}</div>
                </div>
            </div>
        </div>
    );
};

const HistoryTable = ({ signals }) => {
    if (!signals || signals.length === 0) {
        return (
            <div data-testid="apollo-history-empty" className="rounded-2xl bg-neutral-950 border border-neutral-800 p-6 text-center text-neutral-500 text-sm">
                No signals fired yet. Apollo is patient — signals only ship on 2+ gate confluence.
            </div>
        );
    }
    return (
        <div data-testid="apollo-history-table" className="rounded-2xl bg-neutral-950 border border-neutral-800 overflow-hidden">
            <div className="px-5 py-3 border-b border-neutral-800 text-neutral-500 text-xs uppercase tracking-widest">Signal History</div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
                            <th className="text-left px-5 py-2 font-normal">Sent</th>
                            <th className="text-left px-3 py-2 font-normal">Dir</th>
                            <th className="text-left px-3 py-2 font-normal">Entry</th>
                            <th className="text-left px-3 py-2 font-normal">SL</th>
                            <th className="text-left px-3 py-2 font-normal">TP1</th>
                            <th className="text-left px-3 py-2 font-normal">Conf</th>
                            <th className="text-left px-3 py-2 font-normal">Outcome</th>
                        </tr>
                    </thead>
                    <tbody>
                        {signals.map(s => (
                            <tr key={s.signal_id} className="border-t border-neutral-900 hover:bg-neutral-900/40">
                                <td className="px-5 py-3 text-neutral-500 text-xs">{new Date(s.sent_at).toLocaleString()}</td>
                                <td className="px-3 py-3"><DirectionBadge dir={s.direction} /></td>
                                <td className="px-3 py-3 font-mono text-amber-200">${fmt(s.entry)}</td>
                                <td className="px-3 py-3 font-mono text-red-300">${fmt(s.stop_loss)}</td>
                                <td className="px-3 py-3 font-mono text-emerald-300">${fmt((s.take_profit || [])[0])}</td>
                                <td className="px-3 py-3"><ConfidenceBadge conf={s.confidence} /></td>
                                <td className="px-3 py-3 text-xs text-neutral-400 uppercase">{s.outcome || "pending"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const StrategyExplainer = () => (
    <div data-testid="apollo-strategy-card" className="p-5 md:p-6 rounded-2xl bg-neutral-950 border border-neutral-800">
        <div className="text-neutral-500 text-xs uppercase tracking-widest mb-3">The Strategy · 4-Gate Confluence</div>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-neutral-400">
            <div>
                <div className="text-amber-200 font-semibold text-xs uppercase tracking-widest mb-1">Gate 1 · Wyckoff Phase</div>
                <p>Detects accumulation / distribution / markup / markdown on the 4H chart. Directional bias.</p>
            </div>
            <div>
                <div className="text-amber-200 font-semibold text-xs uppercase tracking-widest mb-1">Gate 2 · RSI Divergence</div>
                <p>Bullish / bearish divergences over 30 bars. Momentum confirmation.</p>
            </div>
            <div>
                <div className="text-amber-200 font-semibold text-xs uppercase tracking-widest mb-1">Gate 3 · EMA Cascade</div>
                <p>EMA 20/50/200 alignment. Trend structure filter.</p>
            </div>
            <div>
                <div className="text-amber-200 font-semibold text-xs uppercase tracking-widest mb-1">Gate 4 · Macro Overlay</div>
                <p>DXY + US10Y direction. Signals blocked when fundamental tide is against.</p>
            </div>
        </div>
        <div className="mt-4 text-xs text-neutral-500">
            Signal fires only when <span className="text-amber-200">≥ 2 gates align</span>. HIGH confidence when ≥ 3 align. Stops and targets are ATR-based (adaptive to current volatility regime).
        </div>
    </div>
);

const Header = ({ onRefresh, refreshing }) => (
    <div className="flex items-center justify-between mb-6">
        <div>
            <div className="text-[10px] text-neutral-500 uppercase tracking-[0.3em]">Reset Sanctuary · Guild</div>
            <div className="flex items-center gap-3 mt-1">
                <span data-testid="apollo-header-title" className="text-3xl md:text-4xl font-serif text-amber-200">APOLLO</span>
                <span className="text-xs text-neutral-500 uppercase tracking-widest">Gold Signals · XAU/USD · 4H Swing</span>
            </div>
        </div>
        <button
            data-testid="apollo-refresh-btn"
            onClick={onRefresh}
            disabled={refreshing}
            className="px-3 py-1.5 rounded-lg bg-neutral-900 border border-neutral-800 text-xs uppercase tracking-widest text-neutral-400 hover:bg-neutral-800 hover:text-amber-200 transition disabled:opacity-40"
        >
            {refreshing ? "Scanning…" : "Refresh"}
        </button>
    </div>
);

export default function ApolloPage() {
    const [latest, setLatest] = useState(null);
    const [perf, setPerf] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    const fetchAll = async () => {
        setLoading(true);
        setErr(null);
        try {
            const [l, p, h] = await Promise.all([
                fetch(`${API}/api/apollo/latest`).then(r => r.json()),
                fetch(`${API}/api/apollo/performance`).then(r => r.json()),
                fetch(`${API}/api/apollo/signals?limit=25`).then(r => r.json()),
            ]);
            setLatest(l);
            setPerf(p);
            setHistory(h.signals || []);
        } catch (e) {
            setErr(String(e));
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchAll();
        const t = setInterval(fetchAll, 5 * 60 * 1000); // refresh every 5min
        return () => clearInterval(t);
    }, []);

    const heroSignal = latest?.latest_signal;
    const freshCheck = latest?.fresh_check_result;
    const displayCard = freshCheck?.status === "signal" ? freshCheck : (heroSignal?.status === "signal" ? heroSignal : freshCheck || heroSignal);

    return (
        <div data-testid="apollo-page" className="min-h-screen bg-gradient-to-b from-black via-neutral-950 to-black text-neutral-200">
            <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
                <Header onRefresh={fetchAll} refreshing={loading} />

                <AnimatePresence mode="wait">
                    <HeroSignal signal={displayCard} lastCheck={latest?.last_check} />
                </AnimatePresence>

                {displayCard && displayCard.status !== "signal" && (
                    <StatusBanner status={displayCard.status} reasoning={displayCard.reasoning} />
                )}

                <MacroStrip signal={displayCard} />

                <div className="mt-8">
                    <PerformanceCard perf={perf} />
                </div>

                <div className="mt-8">
                    <StrategyExplainer />
                </div>

                <div className="mt-8">
                    <HistoryTable signals={history} />
                </div>

                {err && (
                    <div data-testid="apollo-error" className="mt-6 rounded-lg border border-red-500/30 bg-red-500/5 p-4 text-red-300 text-xs">
                        {err}
                    </div>
                )}

                <div className="mt-10 text-center text-[10px] text-neutral-600 uppercase tracking-widest">
                    Apollo · Guild Signals · Wyckoff + RSI + EMA + Macro · 4H Swing · Not financial advice.
                </div>
            </div>
        </div>
    );
}
